'use client';
import React, { useState } from 'react';
import { Search, BookOpen, FileText, ChevronRight, X, ArrowLeft, ArrowRight, ExternalLink, Play, Clock, Calendar, Users, Info } from 'lucide-react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { getPublishedBooks } from '../data/books';

// ─── VIDÉOS APTE ───────────────────────────────────────────────────────────────
const videosApte = [
  {
    id: 'video-001',
    titre: "Présentation de l\'association APTE",
    duree: 'Voir sur YouTube',
    date: '2024',
    intervenant: 'Stéphane Saison',
    theme: "Vie de l\'association",
    public: 'Tous publics',
    type: 'Présentation',
    source: 'YouTube — @stefalias4100',
    youtubeUrl: 'https://www.youtube.com/@stefalias4100',
    miniature: '/assets/images/no_image.png',
    altMiniature: "Miniature de la vidéo de présentation de l\'association APTE",
    resume: "Présentation de l'association APTE, de sa mission et de ses actions auprès des sapeurs-pompiers et des personnels de sécurité.",
  },
  {
    id: 'video-002',
    titre: "Conférence ciné-débat : épuisement opérationnel des sapeurs-pompiers",
    duree: 'Voir sur YouTube',
    date: '2024',
    intervenant: 'Stéphane Saison',
    theme: 'Épuisement opérationnel',
    public: 'Sapeurs-pompiers',
    type: 'Conférence',
    source: 'YouTube — @stefalias4100',
    youtubeUrl: 'https://www.youtube.com/@stefalias4100',
    miniature: '/assets/images/no_image.png',
    altMiniature: "Miniature de la conférence ciné-débat sur l\'épuisement opérationnel des sapeurs-pompiers",
    resume: "Conférence et ciné-débat consacrés à l'épuisement opérationnel des sapeurs-pompiers, organisée avec le soutien d'APTE.",
  },
  {
    id: 'video-003',
    titre: "Opération Mont-Saint-Michel 2025",
    duree: 'Voir sur YouTube',
    date: '2025',
    intervenant: 'Association APTE',
    theme: "Vie de l\'association",
    public: 'Tous publics',
    type: 'Événement',
    source: 'YouTube — @stefalias4100',
    youtubeUrl: 'https://www.youtube.com/@stefalias4100',
    miniature: '/assets/images/no_image.png',
    altMiniature: "Miniature de la vidéo de l\'Opération Mont-Saint-Michel 2025 organisée par APTE",
    resume: "Retour sur l'Opération Mont-Saint-Michel 2025, événement organisé par l'association APTE.",
  },
];

// ─── RESSOURCES GÉNÉRALES ──────────────────────────────────────────────────────
const allResources = [
  {
    id: 'res-001',
    title: 'Comprendre le psychotraumatisme',
    summary: "Une introduction claire aux mécanismes du psychotraumatisme lié aux missions opérationnelles. Ce que c'est, comment cela se manifeste, et pourquoi en parler.",
    public: 'Tous publics',
    profession: 'Tous',
    theme: 'Psychotraumatisme',
    format: 'Fiche pratique',
    access: 'public',
    readTime: '5 min',
    level: 'Pour commencer',
    updatedAt: 'Janvier 2026',
    content: `Le psychotraumatisme est une réponse normale à une situation anormale. Lorsqu'une personne est confrontée à un événement perçu comme une menace grave pour son intégrité ou celle d'autrui, son système nerveux peut réagir de manière intense et durable.\n\nCette réaction ne signifie pas une fragilité particulière. Elle peut toucher toute personne, quel que soit son niveau d'expérience ou de formation.\n\nLes manifestations peuvent inclure des reviviscences, des troubles du sommeil, une hypervigilance ou un évitement de certaines situations. Ces signes méritent attention et accompagnement, pas jugement.\n\nAPTE propose un cadre associatif pour accueillir ces situations et orienter vers les bons interlocuteurs.`,
  },
  {
    id: 'res-002', title: 'Après une intervention difficile : premiers repères', summary: 'Que faire dans les heures et jours qui suivent une intervention marquante. Repères pratiques non médicaux pour les premières étapes.', public: 'Sapeurs-pompiers', profession: 'Sapeurs-pompiers', theme: 'Prévention',
    format: 'Fiche pratique', access: 'public', readTime: '7 min', level: 'Pour commencer', updatedAt: 'Février 2026',
    content: `Les premières heures après une intervention difficile sont importantes. Sans chercher à analyser immédiatement ce qui s'est passé, voici quelques repères simples.\n\nPermettez-vous du temps. Inutile de reprendre une activité intense immédiatement si vous en ressentez le besoin.\n\nParlez à quelqu'un de confiance si vous le souhaitez — pas nécessairement pour analyser, mais pour ne pas rester seul avec ce que vous avez vécu.\n\nSi des signes persistants apparaissent après plusieurs jours (troubles du sommeil, reviviscences, irritabilité inhabituelle), c'est le moment d'en parler à quelqu'un. APTE peut vous orienter.`,
  },
  {
    id: 'res-003', title: 'Soutenir un collègue sans le brusquer', summary: "Comment entrer en relation avec un collègue en difficulté, avec bienveillance et respect. Ce qu'on peut dire, ce qu'on évite.", public: 'Aidants', profession: 'Aidants', theme: 'Accompagnement',
    format: 'Article', access: 'public', readTime: '6 min', level: 'Pour commencer', updatedAt: 'Janvier 2026',
    content: `Soutenir un collègue qui traverse une période difficile n'est pas toujours simple. La peur de dire quelque chose de maladroit peut pousser au silence — ce qui peut être vécu comme un abandon.\n\nQuelques principes simples : soyez présent sans forcer la parole. Un "je suis là si tu veux parler" vaut mieux qu'une série de questions.\n\nÉvitez les comparaisons ("moi aussi j'ai vécu des choses dures") et les minimisations ("ça va aller, t'es solide"). Ces formules, même bien intentionnées, peuvent fermer la conversation.\n\nSi vous êtes préoccupé par la situation d'un collègue, vous pouvez contacter APTE pour être orienté sur la meilleure façon d'aider.`,
  },
  {
    id: 'res-004', title: 'Parler à son entourage', summary: "Comment aborder avec sa famille ou ses proches ce que l'on vit dans le cadre de son activité professionnelle, sans les surcharger.", public: 'Tous publics', profession: 'Tous', theme: 'Accompagnement',
    format: 'Article', access: 'public', readTime: '8 min', level: 'Repères essentiels', updatedAt: 'Mars 2026',
    content: `L'entourage familial est souvent le premier témoin des changements liés à un psychotraumatisme — et souvent le moins bien équipé pour y répondre.\n\nParler ne signifie pas tout raconter. Vous pouvez exprimer ce que vous ressentez sans décrire les détails de ce que vous avez vécu.\n\nDes formulations simples peuvent aider : "J'ai vécu quelque chose de difficile, je n'ai pas envie d'en parler pour l'instant, mais je voulais que tu saches que je ne suis pas dans mon état normal."\n\nSi votre entourage ne comprend pas, ce n'est pas forcément un manque de volonté — c'est souvent un manque d'information. APTE propose des ressources spécifiques pour les proches.`,
  },
  {
    id: 'res-005', title: 'Préparer un échange avec un référent APTE', summary: "Ce que vous pouvez anticiper avant votre premier rendez-vous ou échange avec un référent APTE. Aucune préparation n'est obligatoire.", public: 'Personnes concernées', profession: 'Tous', theme: 'Accompagnement',
    format: 'Guide', access: 'public', readTime: '4 min', level: 'Pour commencer', updatedAt: 'Janvier 2026',
    content: `Votre premier échange avec un référent APTE n'a pas de format imposé. Il n'y a rien à préparer en particulier.\n\nCependant, si vous souhaitez vous y préparer, voici ce qui peut être utile : avoir une idée de ce que vous attendez de cet échange (être écouté, obtenir des informations, être orienté), et avoir identifié vos préférences de contact.\n\nLe référent ne vous demandera pas de récit détaillé de ce que vous avez vécu. Il cherchera à comprendre votre situation de façon globale pour vous orienter au mieux.\n\nSi vous ne savez pas quoi dire, c'est normal. "Je ne sais pas par où commencer" est une réponse tout à fait valable.`,
  },
  {
    id: 'res-006', title: 'Prévention pour les encadrants', summary: "Comment identifier les signaux précoces chez les membres de son équipe et créer un environnement propice à la parole.", public: 'Encadrants', profession: 'Encadrants', theme: 'Prévention',
    format: 'Guide', access: 'public', readTime: '10 min', level: 'Repères essentiels', updatedAt: 'Février 2026',
    content: `Les encadrants jouent un rôle crucial dans la prévention des troubles psychotraumatiques. Leur posture peut faciliter ou au contraire inhiber la parole des personnes concernées.\n\nQuelques leviers accessibles : normaliser la parole autour des interventions difficiles (debriefings informels, espaces de parole), observer les changements de comportement sans les juger, et savoir orienter sans diagnostiquer.\n\nIl ne s'agit pas de devenir psychologue, mais de créer un environnement où demander de l'aide n'est pas perçu comme une faiblesse.\n\nAPTE peut intervenir auprès des équipes pour des actions de sensibilisation adaptées.`,
  },
  {
    id: 'res-007', title: "Retour d\'expérience et confidentialité", summary: "Ce que vous devez savoir sur la confidentialité de vos échanges avec APTE et sur le cadre associatif de l'accompagnement.", public: 'Tous publics', profession: 'Tous', theme: 'Accompagnement',
    format: 'Fiche pratique', access: 'public', readTime: '3 min', level: 'Pour commencer', updatedAt: 'Janvier 2026',
    content: `APTE est une association. L'accompagnement proposé est de nature associative et administrative — il ne s'agit pas d'un suivi médical ou thérapeutique.\n\nLes informations que vous partagez avec votre référent APTE sont traitées avec discrétion et dans le cadre des engagements de l'association. Aucune donnée médicale ou clinique n'est collectée ni stockée.\n\nVotre espace personnel sur la plateforme contient uniquement des informations administratives : statut de votre demande, rendez-vous, ressources. Aucun dossier psychologique n'y est conservé.\n\nPour toute question sur le cadre de confidentialité, contactez directement l'équipe APTE.`,
  },
  {
    id: 'res-008', title: 'Demander une intervention de sensibilisation', summary: "Comment solliciter une intervention APTE auprès de votre unité, service ou organisation pour une action de prévention ou de sensibilisation.", public: 'Encadrants', profession: 'Encadrants', theme: 'Sensibilisation',
    format: 'Guide', access: 'public', readTime: '5 min', level: 'Repères essentiels', updatedAt: 'Mars 2026',
    content: `APTE peut intervenir auprès des unités, services et organisations pour des actions de sensibilisation au psychotraumatisme.\n\nCes interventions peuvent prendre différentes formes : présentation générale pour une équipe, session de questions-réponses, formation courte pour les encadrants, ou mise en place d'un dispositif de veille.\n\nPour demander une intervention, vous pouvez utiliser le formulaire de contact disponible sur la plateforme. Précisez le contexte, l'effectif concerné et vos objectifs.\n\nL'équipe APTE vous répondra pour discuter de la forme la plus adaptée à votre situation.`,
  },
  {
    id: 'res-009', title: 'Spécificités du psychotraumatisme en gendarmerie', summary: "Repères sur les contextes opérationnels propres à la gendarmerie et les particularités de l\'accompagnement dans ce cadre institutionnel.", public: 'Gendarmerie', profession: 'Gendarmerie', theme: 'Psychotraumatisme',
    format: 'Fiche pratique', access: 'public', readTime: '8 min', level: 'Repères essentiels', updatedAt: 'Avril 2026',
    content: `Les gendarmes exercent dans un cadre institutionnel particulier, souvent en zone rurale ou périurbaine, avec des missions variées allant de la sécurité publique aux interventions judiciaires.\n\nLa culture du corps, fondée sur la cohésion et la discrétion, peut parfois rendre difficile l'expression des difficultés. Il est important de rappeler que demander de l'aide n'est pas incompatible avec les valeurs du service.\n\nAPTE accompagne les gendarmes et leurs proches dans le respect de ce cadre institutionnel, sans interférer avec les procédures internes de soutien existantes.`,
  },
  {
    id: 'res-010', title: 'Accompagnement des policiers : cadre et démarche', summary: "Présentation du cadre d'accompagnement APTE pour les policiers et les spécificités de la démarche dans ce contexte professionnel.", public: 'Police', profession: 'Police', theme: 'Accompagnement',
    format: 'Guide', access: 'public', readTime: '6 min', level: 'Pour commencer', updatedAt: 'Février 2026',
    content: `Les policiers sont exposés à des situations potentiellement traumatisantes dans le cadre de leurs missions quotidiennes. La démarche APTE est distincte des dispositifs internes à la police nationale. Elle offre un espace associatif indépendant, sans lien avec la hiérarchie ou les procédures disciplinaires.\n\nVous pouvez contacter APTE à titre personnel, sans que cela n'ait d'incidence sur votre situation professionnelle.`,
  },
  {
    id: 'res-011', title: 'Comprendre les mécanismes de dissociation', summary: "Approfondissement sur les réponses dissociatives au traumatisme : ce qu'elles sont, pourquoi elles surviennent et comment les aborder.", public: 'Tous publics', profession: 'Tous', theme: 'Psychotraumatisme',
    format: 'Article', access: 'public', readTime: '12 min', level: 'Approfondissement', updatedAt: 'Mars 2026',
    content: `La dissociation est un mécanisme de protection que le cerveau peut activer face à une expérience perçue comme insupportable. Elle peut se manifester par un sentiment de détachement, une impression de ne pas être dans son corps, ou des lacunes de mémoire autour de l'événement.\n\nCes réponses sont involontaires et ne signifient pas une faiblesse ou un dysfonctionnement. Elles témoignent au contraire de la capacité du système nerveux à se protéger.\n\nComprendre ces mécanismes peut aider à dédramatiser certaines expériences et à mieux communiquer avec un professionnel de santé si vous décidez de consulter.`,
  },
];

// ─── FILTRES ───────────────────────────────────────────────────────────────────
const formatFilters = ['Tous', 'Livres', 'Vidéos', 'Replays', 'Articles', 'Fiches pratiques', 'Guides', 'Témoignages', 'Mémoires'];
const publicFilters = ['Tous publics', 'Sapeurs-pompiers', 'Police', 'Gendarmerie', 'Aidants', 'Encadrants', 'Professionnels', 'Adhérents'];
const themeFilters = ['Tous', 'Psychotraumatisme', 'Épuisement opérationnel', 'Prévention', 'Accompagnement', 'Entourage', "Retour d\'expérience", "Vie de l\'association", 'Sensibilisation'];
const levelOptions = ['Tous', 'Pour commencer', 'Repères essentiels', 'Approfondissement'];

export default function ResourcesLibrary() {
  const [search, setSearch] = useState('');
  const [filterFormat, setFilterFormat] = useState('Tous');
  const [filterPublic, setFilterPublic] = useState('Tous publics');
  const [filterTheme, setFilterTheme] = useState('Tous');
  const [filterLevel, setFilterLevel] = useState('Tous');
  const [selectedResource, setSelectedResource] = useState<typeof allResources[0] | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<typeof videosApte[0] | null>(null);

  const livresApte = getPublishedBooks();

  const filtered = allResources.filter((r) => {
    const matchSearch =
      search === '' ||
      r.title.toLowerCase().includes(search.toLowerCase()) ||
      r.summary.toLowerCase().includes(search.toLowerCase());
    const matchPublic =
      filterPublic === 'Tous publics' ||
      r.profession === filterPublic ||
      r.profession === 'Tous';
    const matchTheme = filterTheme === 'Tous' || r.theme === filterTheme;
    const matchLevel = filterLevel === 'Tous' || r.level === filterLevel;
    const matchFormat =
      filterFormat === 'Tous' ||
      (filterFormat === 'Articles' && r.format === 'Article') ||
      (filterFormat === 'Fiches pratiques' && r.format === 'Fiche pratique') ||
      (filterFormat === 'Guides' && r.format === 'Guide');
    return matchSearch && matchPublic && matchTheme && matchLevel && matchFormat;
  });

  const resetFilters = () => {
    setSearch('');
    setFilterFormat('Tous');
    setFilterPublic('Tous publics');
    setFilterTheme('Tous');
    setFilterLevel('Tous');
  };

  const hasActiveFilters =
    search !== '' || filterFormat !== 'Tous' || filterPublic !== 'Tous publics' || filterTheme !== 'Tous' || filterLevel !== 'Tous';

  const formatIcon = (format: string) => {
    if (format === 'Article') return <FileText size={13} />;
    if (format === 'Guide') return <BookOpen size={13} />;
    return <FileText size={13} />;
  };

  const themeColor = (theme: string) => {
    const map: Record<string, string> = {
      'Psychotraumatisme': 'bg-primary/10 text-primary',
      'Prévention': 'bg-accent/15 text-amber-800',
      'Aidants': 'bg-secondary text-primary',
      'Accompagnement': 'bg-primary/10 text-primary',
      'Sensibilisation': 'bg-accent/15 text-amber-800',
    };
    return map[theme] || 'bg-secondary text-muted-foreground';
  };

  const levelColor = (level: string) => {
    const map: Record<string, string> = {
      'Pour commencer': 'bg-emerald-50 text-emerald-700',
      'Repères essentiels': 'bg-blue-50 text-blue-700',
      'Approfondissement': 'bg-violet-50 text-violet-700',
    };
    return map[level] || 'bg-secondary text-muted-foreground';
  };

  // ─── Vidéo detail view ───────────────────────────────────────────────────────
  if (selectedVideo) {
    return (
      <div className="max-w-3xl mx-auto animate-fade-in">
        <button
          onClick={() => setSelectedVideo(null)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-smooth mb-6"
        >
          <ArrowLeft size={16} />
          Retour aux ressources
        </button>
        <div className="card-apte overflow-hidden">
          <div className="bg-secondary px-6 lg:px-8 py-6 border-b border-border">
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary">{selectedVideo.type}</span>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-accent/15 text-amber-800">{selectedVideo.theme}</span>
            </div>
            <h1 className="text-xl font-bold text-foreground mb-2">{selectedVideo.titre}</h1>
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mt-3">
              <span className="flex items-center gap-1"><Users size={12} />{selectedVideo.intervenant}</span>
              <span className="flex items-center gap-1"><Calendar size={12} />{selectedVideo.date}</span>
              <span className="flex items-center gap-1"><Clock size={12} />{selectedVideo.duree}</span>
            </div>
          </div>
          <div className="px-6 lg:px-8 py-8">
            <p className="text-sm text-foreground leading-relaxed mb-4">{selectedVideo.resume}</p>
            <p className="text-xs text-muted-foreground mb-6">Source : {selectedVideo.source}</p>
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 text-xs text-blue-800 mb-6">
              <strong>Règles de lecture :</strong> Aucune lecture automatique. Cliquez sur le lien pour regarder la vidéo sur YouTube dans un nouvel onglet.
            </div>
          </div>
          <div className="px-6 lg:px-8 py-5 border-t border-border bg-secondary/30 flex flex-col sm:flex-row gap-3">
            <a
              href={selectedVideo.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#0A4C64] hover:bg-[#071923] text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-all duration-200 min-h-[44px]"
            >
              <Play size={14} aria-hidden="true" />
              Regarder sur YouTube
              <ExternalLink size={13} aria-hidden="true" />
            </a>
            <Link href="/tre-accompagn" className="btn-primary text-sm">
              Demander un accompagnement <ArrowRight size={15} />
            </Link>
            <button onClick={() => setSelectedVideo(null)} className="btn-outline text-sm">
              Revenir aux ressources
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Resource detail view ────────────────────────────────────────────────────
  if (selectedResource) {
    return (
      <div className="max-w-3xl mx-auto animate-fade-in">
        <button
          onClick={() => setSelectedResource(null)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-smooth mb-6"
        >
          <ArrowLeft size={16} />
          Retour aux ressources
        </button>
        <div className="card-apte overflow-hidden">
          <div className="bg-secondary px-6 lg:px-8 py-6 border-b border-border">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${themeColor(selectedResource.theme)}`}>{selectedResource.theme}</span>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${levelColor(selectedResource.level)}`}>{selectedResource.level}</span>
              <span className="text-xs text-muted-foreground flex items-center gap-1">{formatIcon(selectedResource.format)}{selectedResource.format}</span>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground">{selectedResource.readTime} de lecture</span>
            </div>
            <h1 className="text-xl font-bold text-foreground mb-2">{selectedResource.title}</h1>
            <p className="text-sm text-muted-foreground">{selectedResource.summary}</p>
            <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span>Public visé : <span className="font-medium text-foreground">{selectedResource.public}</span></span>
              <span>Mis à jour : <span className="font-medium text-foreground">{selectedResource.updatedAt}</span></span>
            </div>
          </div>
          <div className="px-6 lg:px-8 py-8">
            <div className="prose prose-sm max-w-none text-foreground">
              {selectedResource.content.split('\n\n').map((paragraph, i) => (
                <p key={`para-${selectedResource.id}-${i}`} className="text-sm text-foreground leading-relaxed mb-4 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="mt-8 p-4 bg-amber-50 rounded-xl border border-amber-200 text-xs text-amber-800">
              <strong>Rappel :</strong> Ce contenu est informatif et associatif. Il ne constitue pas un avis médical ou psychologique. Si vous avez besoin d&apos;un accompagnement professionnel, APTE peut vous orienter.
            </div>
          </div>
          <div className="px-6 lg:px-8 py-5 border-t border-border bg-secondary/30 flex flex-col sm:flex-row gap-3">
            <Link href="/tre-accompagn" className="btn-primary text-sm">
              Demander un accompagnement <ArrowRight size={15} />
            </Link>
            <button onClick={() => setSelectedResource(null)} className="btn-outline text-sm">
              Revenir aux ressources
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main library view ───────────────────────────────────────────────────────
  return (
    <div>
      {/* ── SECTION LIVRES APTE ─────────────────────────────────────────────── */}
      <section id="livres" className="mb-14" aria-labelledby="livres-title">
        <div className="mb-6">
          <p className="text-xs font-semibold text-[#8A5F00] uppercase tracking-widest mb-2">Priorité éditoriale</p>
          <h2 id="livres-title" className="text-2xl font-bold text-[#071923] mb-1">Les livres APTE</h2>
          <p className="text-sm text-[#536873]">Des récits et réflexions pour comprendre ce que les missions peuvent laisser derrière elles.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {livresApte.map((livre) => (
            <article
              key={livre.id}
              className="bg-white border border-[#D1A338]/25 rounded-2xl overflow-hidden hover:border-[#D1A338]/50 hover:shadow-md transition-all duration-200 flex flex-col"
            >
              {/* Couverture — cadre premium */}
              <div
                className="flex items-center justify-center"
                style={{
                  background: 'linear-gradient(145deg, #F6F7F3 0%, #E9EEEA 100%)',
                  border: '1px solid rgba(10, 76, 100, 0.10)',
                  borderRadius: '16px 16px 0 0',
                  padding: '32px 24px',
                  minHeight: '220px',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)'
                }}
              >
                <img
                  src={livre.cover_image}
                  alt={livre.cover_image_alt}
                  width={livre.slug === 'le-tas' ? 195 : 325}
                  height={livre.slug === 'le-tas' ? 277 : 461}
                  loading="lazy"
                  decoding="async"
                  style={{
                    objectFit: 'contain',
                    objectPosition: 'center',
                    maxWidth: livre.slug === 'le-tas' ? '120px' : '150px',
                    maxHeight: '200px',
                    width: 'auto',
                    height: 'auto',
                    borderRadius: '2px',
                    boxShadow: '0 18px 36px rgba(7, 25, 35, 0.14), 0 4px 10px rgba(7, 25, 35, 0.08)'
                  }}
                />
              </div>

              {/* Contenu */}
              <div className="p-5 flex flex-col gap-3 flex-1">
                <div>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#D1A338]/15 text-[#8A5F00] mb-2 inline-block">Livre</span>
                  <h3 className="font-bold text-[#071923] text-lg leading-snug">{livre.title}</h3>
                  <p className="text-xs text-[#536873] mb-1">par {livre.author}</p>
                  <p className="text-sm font-semibold text-[#0A4C64]">{livre.indicative_price}</p>
                  <p className="text-xs text-[#536873] mt-0.5">Éditeur : {livre.publisher}</p>
                </div>
                <p className="text-sm text-[#536873] leading-relaxed flex-1">{livre.short_description}</p>

                {/* Indicateur vidéos */}
                {livre.videos.filter(v => v.publication_status === 'published').length > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-[#0A4C64]">
                    <Info size={12} aria-hidden="true" />
                    <span>
                      {livre.videos.filter(v => v.publication_status === 'published').length} vidéo
                      {livre.videos.filter(v => v.publication_status === 'published').length > 1 ? 's' : ''} associée
                      {livre.videos.filter(v => v.publication_status === 'published').length > 1 ? 's' : ''}
                    </span>
                  </div>
                )}

                {/* Boutons */}
                <div className="flex flex-col gap-2 pt-2 border-t border-[#E2E8EC]">
                  {/* Bouton principal : fiche interne */}
                  <Link
                    href={`/ressources/livres/${livre.slug}`}
                    className="inline-flex items-center justify-center gap-2 bg-[#071923] hover:bg-[#0A4C64] text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-all duration-200 min-h-[44px] w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4C64]"
                    aria-label={`Découvrir le livre : ${livre.title}`}
                  >
                    Découvrir le livre
                    <ChevronRight size={14} aria-hidden="true" />
                  </Link>

                  {/* Bouton secondaire : achat externe */}
                  {livre.external_link_enabled && (
                    <div className="flex flex-col gap-1">
                      <a
                        href={livre.external_link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={
                          livre.slug === 'lepreuve-du-feu' ? "Acheter L'Épreuve du feu sur le site externe Édilivre" :'Acheter Le Tas sur le site externe Édilivre'
                        }
                        className="inline-flex items-center justify-center gap-2 bg-[#D1A338] hover:bg-[#B8902E] text-[#071923] font-semibold text-sm px-4 py-2.5 rounded-lg transition-all duration-200 min-h-[44px] w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#071923] hover:-translate-y-px active:translate-y-0"
                      >
                        {livre.purchase_cta_label}
                        <ExternalLink size={13} aria-hidden="true" />
                      </a>
                      <p className="text-xs text-[#536873] leading-snug">
                        Vous allez être redirigé vers le site de l&apos;éditeur. APTE ne traite ni la commande, ni le paiement, ni la livraison.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
        <p className="text-xs text-[#536873] mt-4 text-center">
          Prix indicatif communiqué par l&apos;éditeur. La commande est réalisée directement sur le site de l&apos;éditeur. APTE ne collecte aucune donnée de paiement pour cette opération.
        </p>
      </section>

      {/* ── SECTION VIDÉOS APTE ─────────────────────────────────────────────── */}
      <section className="mb-14" aria-labelledby="videos-title">
        <div className="mb-6">
          <p className="text-xs font-semibold text-[#8A5F00] uppercase tracking-widest mb-2">Chaîne officielle</p>
          <h2 id="videos-title" className="text-2xl font-bold text-[#071923] mb-1">Vidéos et replays APTE</h2>
          <p className="text-sm text-[#536873]">Interviews, témoignages, conférences et contenus de sensibilisation proposés ou relayés par l&apos;association.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {videosApte.map((video) => (
            <div
              key={video.id}
              className="bg-white border border-[#0A4C64]/10 rounded-2xl overflow-hidden hover:border-[#0A4C64]/25 hover:shadow-sm transition-all duration-200 flex flex-col"
            >
              <div className="relative bg-[#071923]/10 aspect-video flex items-center justify-center">
                <AppImage
                  src={video.miniature}
                  alt={video.altMiniature}
                  width={320}
                  height={180}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-[#071923]/40">
                  <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-md">
                    <Play size={20} className="text-[#071923] ml-0.5" aria-hidden="true" />
                  </div>
                </div>
              </div>
              <div className="p-4 flex flex-col gap-3 flex-1">
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">{video.type}</span>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-accent/15 text-amber-800">{video.theme}</span>
                </div>
                <h3 className="font-bold text-[#071923] text-sm leading-snug flex-1">{video.titre}</h3>
                <div className="flex flex-wrap gap-3 text-xs text-[#536873]">
                  <span className="flex items-center gap-1"><Users size={11} />{video.intervenant}</span>
                  <span className="flex items-center gap-1"><Calendar size={11} />{video.date}</span>
                </div>
                <div className="flex gap-2 pt-2 border-t border-border">
                  <button
                    onClick={() => setSelectedVideo(video)}
                    className="text-xs font-semibold text-[#0A4C64] hover:text-[#071923] flex items-center gap-1 transition-all duration-200 min-h-[44px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#0A4C64] rounded"
                    aria-label={`Voir les détails de la vidéo : ${video.titre}`}
                  >
                    Détails <ChevronRight size={13} />
                  </button>
                  <a
                    href={video.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold text-[#536873] hover:text-[#0A4C64] flex items-center gap-1 transition-all duration-200 min-h-[44px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#0A4C64] rounded"
                    aria-label={`Regarder ${video.titre} sur YouTube (nouvel onglet)`}
                  >
                    Regarder <ExternalLink size={11} />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── MOTEUR DE RECHERCHE ET FILTRES ──────────────────────────────────── */}
      <section aria-labelledby="bibliotheque-title">
        <div className="mb-6">
          <h2 id="bibliotheque-title" className="text-2xl font-bold text-[#071923] mb-1">Toutes les ressources</h2>
          <p className="text-sm text-[#536873]">Fiches pratiques, articles, guides et contenus de prévention.</p>
        </div>

        <div className="mb-5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Format</p>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filtres par format">
            {formatFilters.map((fmt) => (
              <button
                key={`fmt-filter-${fmt}`}
                onClick={() => setFilterFormat(fmt)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4C64] focus-visible:ring-offset-1 ${
                  filterFormat === fmt
                    ? 'bg-[#0A4C64] text-white shadow-sm'
                    : 'bg-white border border-border text-[#536873] hover:border-[#0A4C64]/40 hover:text-[#0A4C64]'
                }`}
                aria-pressed={filterFormat === fmt}
              >
                {fmt}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Public</p>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filtres par public">
            {publicFilters.map((pub) => (
              <button
                key={`pub-filter-${pub}`}
                onClick={() => setFilterPublic(pub)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4C64] focus-visible:ring-offset-1 ${
                  filterPublic === pub
                    ? 'bg-[#0A4C64] text-white shadow-sm'
                    : 'bg-white border border-border text-[#536873] hover:border-[#0A4C64]/40 hover:text-[#0A4C64]'
                }`}
                aria-pressed={filterPublic === pub}
              >
                {pub}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border p-5 mb-8 shadow-card">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par titre, auteur, thème..."
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-smooth"
                aria-label="Rechercher dans les ressources"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="filter-theme" className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Thème</label>
              <select
                id="filter-theme"
                value={filterTheme}
                onChange={(e) => setFilterTheme(e.target.value)}
                className="px-3 py-2.5 rounded-lg border border-border text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring transition-smooth min-w-[200px]"
              >
                {themeFilters.map((opt) => (
                  <option key={`theme-opt-${opt}`} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="filter-level" className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Niveau</label>
              <select
                id="filter-level"
                value={filterLevel}
                onChange={(e) => setFilterLevel(e.target.value)}
                className="px-3 py-2.5 rounded-lg border border-border text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring transition-smooth min-w-[180px]"
              >
                {levelOptions.map((opt) => (
                  <option key={`level-opt-${opt}`} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{filtered.length}</span> résultat{filtered.length !== 1 ? 's' : ''}
              </p>
              <button
                onClick={resetFilters}
                className="text-xs text-danger hover:text-danger/80 flex items-center gap-1 transition-smooth font-medium"
              >
                <X size={13} />
                Réinitialiser les filtres
              </button>
            </div>
          )}
        </div>

        {!hasActiveFilters && (
          <p className="text-sm text-muted-foreground mb-5">
            <span className="font-semibold text-foreground">{allResources.length}</span> ressources disponibles · Aucun compte n&apos;est nécessaire pour consulter les ressources publiques.
          </p>
        )}

        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
              <BookOpen size={28} className="text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-foreground mb-2">Aucune ressource trouvée</h3>
            <p className="text-sm text-muted-foreground mb-4">Essayez de modifier vos filtres ou votre recherche.</p>
            <button onClick={resetFilters} className="btn-outline text-sm">Réinitialiser les filtres</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
            {filtered.map((res) => (
              <button
                key={res.id}
                onClick={() => setSelectedResource(res)}
                className="card-apte p-5 flex flex-col gap-4 text-left w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`Lire la ressource : ${res.title}`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${themeColor(res.theme)}`}>{res.theme}</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${levelColor(res.level)}`}>{res.level}</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground text-sm mb-2 leading-snug line-clamp-2">{res.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">{res.summary}</p>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-muted-foreground">{res.public}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">{formatIcon(res.format)}{res.format}</span>
                      <span className="text-xs text-muted-foreground">· {res.readTime}</span>
                    </div>
                    <span className="text-xs text-muted-foreground/70">Mis à jour : {res.updatedAt}</span>
                  </div>
                  <span className="text-xs font-semibold text-accent flex items-center gap-1 transition-smooth">
                    Lire <ChevronRight size={13} />
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="mt-12 bg-secondary rounded-xl p-6 lg:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-semibold text-foreground mb-1">Vous ne trouvez pas ce que vous cherchez ?</h3>
            <p className="text-sm text-muted-foreground">Un référent APTE peut vous orienter vers les ressources adaptées à votre situation.</p>
          </div>
          <Link href="/tre-accompagn" className="btn-primary text-sm flex-shrink-0">
            Demander une orientation <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
}